import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Cube, HardHat, MagnifyingGlass, PencilSimple, MapPin, DownloadSimple } from "@phosphor-icons/react";

export default function InventoryPage() {
  const { user } = useAuth();
  // Adjust physical qty is now supervisor-only, and only via the Parts tab
  // "Receive Shipment" tool — not from here.
  const canAdjust = false;
  const canManagerAdjustEngineer = ["supervisor", "manager"].includes(user.role);
  const [adjTarget, setAdjTarget] = useState(null); // engineer inventory row being adjusted
  const isEngineer = user.role === "engineer";
  const [searchParams] = useSearchParams();
  const initialEngineer = searchParams.get("engineer") || "all";

  const [tab, setTab] = useState(isEngineer ? "engineer" : (initialEngineer !== "all" ? "engineer" : "store"));
  const [stock, setStock] = useState([]);
  const [inv, setInv] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [engineerFilter, setEngineerFilter] = useState(initialEngineer);
  const [regionFilter, setRegionFilter] = useState("all");
  const [groupFilter, setGroupFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [expandedRegion, setExpandedRegion] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (groupFilter !== "all") params.group = groupFilter;
      const invParams = {};
      // For the region tab we always need the full inventory to pivot; individual
      // engineer filter is meaningless there.
      if (!isEngineer && tab !== "region" && engineerFilter !== "all") invParams.engineer_id = engineerFilter;
      const [st, iv, eng] = await Promise.all([
        api.get("/stock", { params: { limit: 10000, ...(groupFilter !== "all" ? { group: groupFilter } : {}) } }),
        api.get("/inventory", { params: invParams }),
        isEngineer ? Promise.resolve({ data: [] }) : api.get("/engineers"),
      ]);
      setStock(st.data);
      setInv(iv.data);
      setEngineers(eng.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [groupFilter, engineerFilter, tab]);

  const filteredStock = useMemo(() => {
    const q = search.trim().toLowerCase();
    return stock.filter((s) => !q || `${s.item} ${s.description}`.toLowerCase().includes(q));
  }, [stock, search]);
  const filteredInv = useMemo(() => {
    const q = search.trim().toLowerCase();
    return inv.filter((i) => !q || `${i.name} ${i.group || ""}`.toLowerCase().includes(q));
  }, [inv, search]);

  // Region-pivoted view: Region → total qty + list of (part, qty, engineers[]).
  // Uses the current search filter but ignores the engineer filter (region tab
  // always shows the full picture across every engineer).
  const engineerById = useMemo(() => {
    const m = {};
    engineers.forEach((e) => { m[e.id] = e; });
    return m;
  }, [engineers]);
  const regionPivot = useMemo(() => {
    const q = search.trim().toLowerCase();
    const buckets = new Map(); // region -> { region, total_qty, parts: Map(key -> {name, group, stock_code, total, byEngineer:[]}) }
    inv.forEach((it) => {
      if (Number(it.qty || 0) <= 0) return;
      if (q && !`${it.name} ${it.group || ""}`.toLowerCase().includes(q)) return;
      const eng = engineerById[it.engineer_id];
      const region = (eng && eng.region) || "— Unassigned —";
      if (!buckets.has(region)) buckets.set(region, { region, total_qty: 0, parts: new Map() });
      const bucket = buckets.get(region);
      bucket.total_qty += Number(it.qty || 0);
      const key = (it.stock_code || it.name || "").toLowerCase();
      if (!bucket.parts.has(key)) {
        bucket.parts.set(key, {
          key,
          name: it.name,
          group: it.group || null,
          stock_code: it.stock_code || null,
          total: 0,
          byEngineer: [],
        });
      }
      const part = bucket.parts.get(key);
      part.total += Number(it.qty || 0);
      part.byEngineer.push({
        engineer_id: it.engineer_id,
        engineer_name: eng ? eng.name : `Unknown (${(it.engineer_id || "").slice(0, 8)})`,
        qty: Number(it.qty || 0),
      });
    });
    // Convert Maps → arrays, sort regions alpha, parts by qty desc, engineers by qty desc.
    return Array.from(buckets.values())
      .filter((b) => regionFilter === "all" || b.region === regionFilter)
      .map((b) => ({
        region: b.region,
        total_qty: b.total_qty,
        parts_count: b.parts.size,
        parts: Array.from(b.parts.values())
          .map((p) => ({ ...p, byEngineer: p.byEngineer.sort((a, c) => c.qty - a.qty) }))
          .sort((a, c) => c.total - a.total),
      }))
      .sort((a, c) => a.region.localeCompare(c.region));
  }, [inv, engineerById, search, regionFilter]);
  const regionOptions = useMemo(() => {
    const set = new Set();
    engineers.forEach((e) => { if (e.region) set.add(e.region); });
    return Array.from(set).sort();
  }, [engineers]);

  const downloadRegionXlsx = async () => {
    try {
      const params = regionFilter !== "all" ? { region: regionFilter } : {};
      const res = await api.get("/inventory/by-region/export.xlsx", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `inventory-by-region-${new Date().toISOString().slice(0, 10)}${regionFilter !== "all" ? `-${regionFilter}` : ""}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Region snapshot downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const groups = Array.from(new Set(stock.map((s) => s.group))).sort();

  return (
    <div className="min-h-screen">
      <div className="border-b border-neutral-200 bg-white/80 backdrop-blur-xl sticky top-0 z-10 no-print">
        <div className="px-10 py-6">
          <div className="label-eyebrow">Stock</div>
          <h1 className="font-display text-4xl tracking-tight leading-none mt-2">Inventory</h1>
          <p className="text-sm text-neutral-500 mt-2 max-w-xl">
            {isEngineer
              ? "Your on-hand parts. Deducted when you use or return a part."
              : "Physical store stock + per-technician on-hand. Adjust the store qty when receiving new stock or performing a count."}
          </p>
          <div className="mt-4 flex gap-2 items-center">
            {!isEngineer && (
              <>
                <Button
                  variant={tab === "store" ? "default" : "outline"}
                  onClick={() => setTab("store")}
                  className={`h-9 rounded-none ${tab === "store" ? "bg-neutral-900 text-white" : "border-neutral-900"}`}
                  data-testid="tab-store"
                >
                  <Cube size={14} weight="bold" className="mr-2" /> Store stock
                </Button>
                <Button
                  variant={tab === "engineer" ? "default" : "outline"}
                  onClick={() => setTab("engineer")}
                  className={`h-9 rounded-none ${tab === "engineer" ? "bg-neutral-900 text-white" : "border-neutral-900"}`}
                  data-testid="tab-engineer"
                >
                  <HardHat size={14} weight="bold" className="mr-2" /> Engineer on-hand
                </Button>
                <Button
                  variant={tab === "region" ? "default" : "outline"}
                  onClick={() => setTab("region")}
                  className={`h-9 rounded-none ${tab === "region" ? "bg-neutral-900 text-white" : "border-neutral-900"}`}
                  data-testid="tab-region"
                >
                  <MapPin size={14} weight="bold" className="mr-2" /> By region
                </Button>
              </>
            )}
          </div>
        </div>
        <div className="px-10 pb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative col-span-2">
            <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search item or description…" className="h-10 pl-9 rounded-none border-neutral-300" data-testid="inv-search" />
          </div>
          {tab === "store" && (
            <Select value={groupFilter} onValueChange={setGroupFilter}>
              <SelectTrigger data-testid="inv-group" className="h-10 rounded-none border-neutral-300"><SelectValue placeholder="Group" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All groups</SelectItem>
                {groups.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {tab === "engineer" && !isEngineer && (
            <Select value={engineerFilter} onValueChange={setEngineerFilter}>
              <SelectTrigger data-testid="inv-engineer" className="h-10 rounded-none border-neutral-300"><SelectValue placeholder="Engineer" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All engineers</SelectItem>
                {engineers.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {tab === "region" && !isEngineer && (
            <Select value={regionFilter} onValueChange={setRegionFilter}>
              <SelectTrigger data-testid="inv-region" className="h-10 rounded-none border-neutral-300"><SelectValue placeholder="Region" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All regions ({regionOptions.length})</SelectItem>
                {regionOptions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      <div className="p-10">
        {tab === "store" && (
          <div className="border border-neutral-200 bg-white overflow-x-auto">
            <table className="w-full text-sm" data-testid="store-stock-table">
              <thead className="border-b border-neutral-200 bg-neutral-50">
                <tr className="text-left">
                  <th className="px-3 py-3 label-eyebrow">Item</th>
                  <th className="px-3 py-3 label-eyebrow">Description</th>
                  <th className="px-3 py-3 label-eyebrow">Group</th>
                  <th className="px-3 py-3 label-eyebrow">UOM</th>
                  <th className="px-3 py-3 label-eyebrow text-right">On Hand</th>
                  {canAdjust && <th className="px-3 py-3 label-eyebrow">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={canAdjust ? 6 : 5} className="px-4 py-10 text-center text-neutral-400">Loading…</td></tr>}
                {!loading && filteredStock.length === 0 && (
                  <tr><td colSpan={canAdjust ? 6 : 5} className="px-4 py-10 text-center text-neutral-400">No items match your filters.</td></tr>
                )}
                {filteredStock.slice(0, 500).map((s) => (
                  <tr key={s.id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50/60" data-testid={`stock-row-${s.id}`}>
                    <td className="px-3 py-2 font-medium text-xs">{s.item}</td>
                    <td className="px-3 py-2 text-xs text-neutral-600">{s.description}</td>
                    <td className="px-3 py-2"><span className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider bg-neutral-100 text-neutral-700">{s.group}</span></td>
                    <td className="px-3 py-2 text-xs text-neutral-500 font-mono">{s.uom}</td>
                    <td className={`px-3 py-2 text-right font-mono ${(s.on_hand_qty || 0) === 0 ? "text-red-700 font-semibold" : ""}`}>{s.on_hand_qty || 0}</td>
                    {canAdjust && (
                      <td className="px-3 py-2">
                        <button onClick={() => setEditing(s)} className="p-1.5 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white" title="Adjust" data-testid={`adjust-btn-${s.id}`}>
                          <PencilSimple size={14} weight="bold" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredStock.length > 500 && (
              <div className="p-3 text-xs text-neutral-500 border-t border-neutral-100">Showing first 500 items. Narrow the filter to see more.</div>
            )}
          </div>
        )}
        {tab === "engineer" && (
          <div className="border border-neutral-200 bg-white overflow-x-auto">
            <table className="w-full text-sm" data-testid="engineer-inventory-table">
              <thead className="border-b border-neutral-200 bg-neutral-50">
                <tr className="text-left">
                  <th className="px-3 py-3 label-eyebrow">Engineer</th>
                  <th className="px-3 py-3 label-eyebrow">Part</th>
                  <th className="px-3 py-3 label-eyebrow">Group</th>
                  <th className="px-3 py-3 label-eyebrow text-right">Qty on hand</th>
                  {canManagerAdjustEngineer && <th className="px-3 py-3 label-eyebrow no-print">Adjust</th>}
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={canManagerAdjustEngineer ? 5 : 4} className="px-4 py-10 text-center text-neutral-400">Loading…</td></tr>}
                {!loading && filteredInv.length === 0 && (
                  <tr><td colSpan={canManagerAdjustEngineer ? 5 : 4} className="px-4 py-10 text-center text-neutral-400">No holdings.</td></tr>
                )}
                {filteredInv.map((it, idx) => {
                  const found = isEngineer ? null : engineers.find((e) => e.id === it.engineer_id);
                  const engineerName = isEngineer
                    ? user.name
                    : (found ? found.name : `Unknown (${it.engineer_id.slice(0, 8)})`);
                  return (
                    <tr key={`${it.engineer_id}-${it.stock_code || it.name}-${idx}`} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50/60" data-testid={`eng-inv-row-${idx}`}>
                      <td className="px-3 py-2 text-xs font-medium">{engineerName}</td>
                      <td className="px-3 py-2 text-xs">
                        {it.name}
                        {it.stock_code == null && <span className="ml-2 text-[9px] font-mono uppercase tracking-wider bg-amber-100 text-amber-800 px-1 py-0.5">custom</span>}
                      </td>
                      <td className="px-3 py-2">
                        {it.group ? <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider bg-neutral-100 text-neutral-700">{it.group}</span> : <span className="text-neutral-400 text-xs">—</span>}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono ${it.qty > 0 ? "" : "text-neutral-400"}`}>{it.qty}</td>
                      {canManagerAdjustEngineer && (
                        <td className="px-3 py-2 no-print">
                          <button
                            onClick={() => setAdjTarget({ ...it, engineerName })}
                            data-testid={`eng-inv-adjust-${idx}`}
                            className="p-1.5 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white"
                            title="Adjust engineer qty (mirrors on main stock)"
                          >
                            <PencilSimple size={14} weight="bold" />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {tab === "region" && (
          <div className="space-y-4" data-testid="region-inventory-view">
            {/* Actions row */}
            <div className="flex items-center justify-end">
              <Button
                onClick={downloadRegionXlsx}
                data-testid="region-export-xlsx"
                variant="outline"
                className="rounded-none border-emerald-700 text-emerald-800 hover:bg-emerald-700 hover:text-white h-10"
                title={regionFilter === "all"
                  ? "Download the full region snapshot as Excel"
                  : `Download snapshot for ${regionFilter} only`}
              >
                <DownloadSimple size={14} weight="bold" className="mr-2" />
                Export Excel{regionFilter !== "all" ? ` · ${regionFilter}` : ""}
              </Button>
            </div>
            {/* KPI row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="border border-neutral-200 bg-white px-4 py-3">
                <div className="label-eyebrow">Regions with stock</div>
                <div className="mt-1 font-display text-2xl">{regionPivot.length}</div>
              </div>
              <div className="border border-neutral-200 bg-white px-4 py-3">
                <div className="label-eyebrow">Total on-hand across engineers</div>
                <div className="mt-1 font-display text-2xl font-mono">
                  {regionPivot.reduce((s, r) => s + r.total_qty, 0).toLocaleString()}
                </div>
              </div>
              <div className="border border-neutral-900 bg-neutral-900 text-white px-4 py-3">
                <div className="label-eyebrow opacity-70">Distinct parts in field</div>
                <div className="mt-1 font-display text-2xl font-mono">
                  {new Set(regionPivot.flatMap((r) => r.parts.map((p) => p.key))).size}
                </div>
              </div>
            </div>

            {/* Region rows — click a region to drill into its parts + who holds them */}
            {loading && <div className="p-6 text-center text-xs text-neutral-400 border border-neutral-200 bg-white">Loading…</div>}
            {!loading && regionPivot.length === 0 && (
              <div className="p-6 text-center text-xs text-neutral-400 border border-neutral-200 bg-white">
                No engineer holdings match the current filters.
              </div>
            )}
            {!loading && regionPivot.map((r) => {
              const open = expandedRegion === r.region;
              return (
                <div key={r.region} className="border border-neutral-200 bg-white" data-testid={`region-block-${r.region}`}>
                  <button
                    type="button"
                    onClick={() => setExpandedRegion(open ? null : r.region)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-neutral-50 text-left"
                    data-testid={`region-toggle-${r.region}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="inline-block w-3 text-neutral-500 select-none">{open ? "▾" : "▸"}</span>
                      <MapPin size={16} weight="duotone" className="text-neutral-500" />
                      <span className="font-medium">{r.region}</span>
                      <span className="text-[10px] font-mono uppercase tracking-wider bg-neutral-100 text-neutral-700 px-1.5 py-0.5">
                        {r.parts_count} part{r.parts_count === 1 ? "" : "s"}
                      </span>
                    </div>
                    <div className="font-mono text-sm">
                      <span className="text-neutral-500 text-xs mr-2">total units</span>
                      <span className="font-semibold">{r.total_qty.toLocaleString()}</span>
                    </div>
                  </button>
                  {open && (
                    <div className="border-t border-neutral-200 overflow-x-auto">
                      <table className="w-full text-sm" data-testid={`region-parts-table-${r.region}`}>
                        <thead className="bg-neutral-50">
                          <tr className="text-left">
                            <th className="px-4 py-2 label-eyebrow">Part</th>
                            <th className="px-4 py-2 label-eyebrow">Group</th>
                            <th className="px-4 py-2 label-eyebrow text-right">Qty in region</th>
                            <th className="px-4 py-2 label-eyebrow">Held by</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.parts.map((p, i) => (
                            <tr key={`${r.region}-${p.key}-${i}`} className="border-t border-neutral-100" data-testid={`region-part-row-${r.region}-${i}`}>
                              <td className="px-4 py-2 text-xs">
                                <div className="font-medium">{p.name}</div>
                                {p.stock_code && <div className="text-[10px] font-mono text-neutral-500">{p.stock_code}</div>}
                              </td>
                              <td className="px-4 py-2">
                                {p.group
                                  ? <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider bg-neutral-100 text-neutral-700">{p.group}</span>
                                  : <span className="text-neutral-400 text-xs">—</span>}
                              </td>
                              <td className="px-4 py-2 text-right font-mono font-semibold">{p.total}</td>
                              <td className="px-4 py-2 text-xs">
                                <div className="flex flex-wrap gap-1.5">
                                  {p.byEngineer.map((e) => (
                                    <span
                                      key={e.engineer_id}
                                      className="inline-flex items-center gap-1.5 border border-neutral-300 px-1.5 py-0.5 text-[10px]"
                                      title={`${e.engineer_name} · ${e.qty}`}
                                      data-testid={`region-eng-chip-${e.engineer_id}`}
                                    >
                                      <span>{e.engineer_name}</span>
                                      <span className="font-mono font-semibold">{e.qty}</span>
                                    </span>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {canAdjust && editing && (
        <AdjustDialog
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {canManagerAdjustEngineer && adjTarget && (
        <EngineerInventoryAdjustDialog
          target={adjTarget}
          onClose={() => setAdjTarget(null)}
          onSaved={() => { setAdjTarget(null); load(); }}
        />
      )}
    </div>
  );
}


function EngineerInventoryAdjustDialog({ target, onClose, onSaved }) {
  const [newQty, setNewQty] = useState(target.qty);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const delta = Number(newQty || 0) - Number(target.qty || 0);
  const isCustom = !target.stock_code;

  const submit = async () => {
    if (delta === 0) return toast.error("New qty must differ from current");
    setBusy(true);
    try {
      const { data } = await api.post("/inventory/adjust", {
        engineer_id: target.engineer_id,
        stock_code: target.stock_code || null,
        name: target.name,
        delta,
        reason,
      });
      const engMsg = `Engineer ${data.engineer.before} → ${data.engineer.after}`;
      const stockMsg = data.main_stock
        ? ` · main stock ${data.main_stock.before} → ${data.main_stock.after}`
        : " · non-master part (main stock not affected)";
      toast.success(engMsg + stockMsg);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md rounded-none border-neutral-900" data-testid="eng-inv-adjust-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl tracking-tight">Adjust Engineer Qty</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm">
            <div><span className="text-neutral-500">Engineer:</span> <b>{target.engineerName}</b></div>
            <div><span className="text-neutral-500">Part:</span> <b>{target.name}</b>
              {target.stock_code && <span className="text-[10px] font-mono text-neutral-500 ml-2">{target.stock_code}</span>}
            </div>
            <div className="text-[11px] text-neutral-500 font-mono mt-1">Current qty in hand: {target.qty}</div>
          </div>
          <div className="space-y-1">
            <Label className="label-eyebrow">New qty in hand</Label>
            <Input
              type="number" min={0}
              value={newQty}
              onChange={(e) => setNewQty(e.target.value)}
              className="rounded-none border-neutral-400 h-10 font-mono"
              data-testid="eng-inv-adjust-qty"
            />
            {delta !== 0 && (
              <div className={`text-[11px] font-mono ${delta > 0 ? "text-red-700" : "text-emerald-700"}`}>
                {delta > 0
                  ? `+${delta} to engineer · -${delta} from main stock`
                  : `${delta} to engineer · +${Math.abs(delta)} to main stock`}
                {isCustom && <span className="ml-2 text-amber-700">(custom part — main stock unaffected)</span>}
              </div>
            )}
          </div>
          <div className="space-y-1">
            <Label className="label-eyebrow">Reason</Label>
            <Input
              placeholder="e.g. physical count correction, lost & found…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="rounded-none border-neutral-400 h-10"
              data-testid="eng-inv-adjust-reason"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">Cancel</Button>
          <Button onClick={submit} disabled={busy || delta === 0} data-testid="eng-inv-adjust-submit" className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed">
            {busy ? "Adjusting…" : "Confirm Adjust"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdjustDialog({ item, onClose, onSaved }) {
  const [qty, setQty] = useState(item.on_hand_qty || 0);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/stock/${item.id}/qty`, { on_hand_qty: Math.max(0, Number(qty) || 0), reason });
      toast.success("Stock updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md rounded-none border-neutral-900" data-testid="adjust-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Adjust stock</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <div className="text-xs font-mono text-neutral-500">{item.group} · {item.uom}</div>
            <div className="text-sm font-medium mt-0.5">{item.item}</div>
            {item.description && item.description !== item.item && (
              <div className="text-xs text-neutral-500 mt-0.5">{item.description}</div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">On-hand qty</Label>
            <Input type="number" min={0} value={qty} onChange={(e) => setQty(e.target.value)} className="rounded-none border-neutral-400 font-mono" data-testid="adjust-qty" autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Reason (audit)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Received PO #123 / Physical count" className="rounded-none border-neutral-400" data-testid="adjust-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="adjust-submit" className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800">
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
